export const CATEGORY_BADGE_STYLES: Record<string, string> = {
  announcements:
    "bg-orange-100 text-orange-800 border-orange-200 dark:border-orange-700 dark:bg-orange-950/50 dark:text-orange-200",
  debugging:
    "bg-red-100 text-red-800 border-red-200 dark:border-red-700 dark:bg-red-950/50 dark:text-red-200",
  showcase:
    "bg-purple-100 text-purple-800 border-purple-200 dark:border-purple-700 dark:bg-purple-950/50 dark:text-purple-200",
  "tips-tricks":
    "bg-green-100 text-green-800 border-green-200 dark:border-green-700 dark:bg-green-950/50 dark:text-green-200",
  "feature-requests":
    "bg-amber-100 text-amber-800 border-amber-200 dark:border-amber-700 dark:bg-amber-950/50 dark:text-amber-200",
  general:
    "bg-slate-100 text-slate-800 border-slate-200 dark:border-zinc-600 dark:bg-zinc-800 dark:text-zinc-100",
  challenges:
    "bg-indigo-100 text-indigo-800 border-indigo-200 dark:border-indigo-700 dark:bg-indigo-950/50 dark:text-indigo-200",
  // Common seeded / renamed slugs that may not match the core set above
  "workflow-builder":
    "bg-sky-100 text-sky-800 border-sky-200 dark:border-sky-700 dark:bg-sky-950/50 dark:text-sky-200",
  "policies-and-rules":
    "bg-violet-100 text-violet-800 border-violet-200 dark:border-violet-700 dark:bg-violet-950/50 dark:text-violet-200",
  policies:
    "bg-violet-100 text-violet-800 border-violet-200 dark:border-violet-700 dark:bg-violet-950/50 dark:text-violet-200",
  workflows:
    "bg-sky-100 text-sky-800 border-sky-200 dark:border-sky-700 dark:bg-sky-950/50 dark:text-sky-200",
};

/** Matches community listing “Create post” and other primary CTAs. */
const COMMUNITY_BRAND_GRADIENT =
  "linear-gradient(93.53deg, #535EA4 14.58%, #8095D4 110.19%)" as const;

export const communityBrandPrimaryClassName =
  "border-0 text-white shadow-none hover:opacity-95 hover:text-white disabled:pointer-events-none disabled:opacity-50";

export const communityBrandPrimaryStyle = {
  background: COMMUNITY_BRAND_GRADIENT,
  border: "none",
} as const;

/** Ant Design `type="primary"` — gradient may still lighten slightly on Ant hover. */
export const communityAntPrimaryButtonProps = {
  className:
    "border-none! text-white! hover:text-white! hover:opacity-95! disabled:opacity-50!",
  style: {
    background: COMMUNITY_BRAND_GRADIENT,
    border: "none",
    color: "#fff",
  } as const,
};

/** Outline / secondary actions aligned to community purple (e.g. Reply). */
export const communityBrandOutlineButtonClassName =
  "border-[#535EA4]/40 text-[#535EA4] hover:bg-[#535EA4]/10 hover:text-[#535EA4] dark:border-indigo-400/40 dark:text-indigo-300 dark:hover:bg-indigo-500/10 dark:hover:text-indigo-200";

export function formatCommunityDate(value: string | Date) {
  return new Date(value).toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
}

export function formatCompactNumber(value: number) {
  return new Intl.NumberFormat("en-US", {
    notation: "compact",
    maximumFractionDigits: 1,
  }).format(value);
}

export function getCommunityDisplayName(user: {
  name?: string | null;
  username?: string | null;
  email?: string | null;
}) {
  return user.name || user.username || user.email || "Community member";
}

const COMMUNITY_AVATAR_FALLBACK_CLASSES = [
  "bg-rose-100 text-rose-700 dark:bg-rose-950/40 dark:text-rose-300",
  "bg-amber-100 text-amber-700 dark:bg-amber-950/40 dark:text-amber-300",
  "bg-emerald-100 text-emerald-700 dark:bg-emerald-950/40 dark:text-emerald-300",
  "bg-sky-100 text-sky-700 dark:bg-sky-950/40 dark:text-sky-300",
  "bg-indigo-100 text-indigo-700 dark:bg-indigo-950/40 dark:text-indigo-300",
  "bg-violet-100 text-violet-700 dark:bg-violet-950/40 dark:text-violet-300",
  "bg-fuchsia-100 text-fuchsia-700 dark:bg-fuchsia-950/40 dark:text-fuchsia-300",
  "bg-cyan-100 text-cyan-700 dark:bg-cyan-950/40 dark:text-cyan-300",
] as const;

export function getCommunityAvatarFallbackLabel(user: {
  name?: string | null;
  username?: string | null;
  email?: string | null;
}) {
  const displayName = getCommunityDisplayName(user).trim();
  return displayName.charAt(0).toUpperCase() || "C";
}

export function getCommunityAvatarFallbackClassName(user: {
  name?: string | null;
  username?: string | null;
  email?: string | null;
}) {
  const displayName = getCommunityDisplayName(user);
  const hash = Array.from(displayName).reduce((total, character) => {
    return total + character.charCodeAt(0);
  }, 0);

  return COMMUNITY_AVATAR_FALLBACK_CLASSES[
    hash % COMMUNITY_AVATAR_FALLBACK_CLASSES.length
  ];
}

const AUTOMATED_AWARD_PREFIX = /^Automated award /i;

/** Normalize legacy DB reasons and display copy for badge feed / admin lists. */
export function formatBadgeAwardLine(
  badgeName: string,
  reason?: string | null,
): string {
  const r = reason?.trim();
  if (!r || AUTOMATED_AWARD_PREFIX.test(r)) {
    switch (badgeName) {
      case "First Post":
        return "Published a first community post.";
      case "Conversation Starter":
        return "Started 5 community discussions.";
      case "Helpful Solver":
        return "Shared a reply that was accepted as the solution.";
      case "Top Contributor":
        return "Reached 50 community reputation points.";
      case "Challenge Participant":
        return "Submitted an entry to a community challenge.";
      default:
        return `Unlocked the '${badgeName}' badge.`;
    }
  }
  return r;
}

export type ThreadableItem = {
  id: string;
  parentId?: string | null;
};

export type ThreadNode<T> = T & {
  children: Array<ThreadNode<T>>;
  depth: number;
};

export function buildThreadTree<T extends ThreadableItem>(items: T[]) {
  const nodeMap = new Map<string, ThreadNode<T>>();
  const roots: Array<ThreadNode<T>> = [];

  for (const item of items) {
    nodeMap.set(item.id, {
      ...item,
      children: [],
      depth: 0,
    });
  }

  for (const item of items) {
    const node = nodeMap.get(item.id);
    if (!node) continue;

    if (item.parentId) {
      const parent = nodeMap.get(item.parentId);
      if (parent) {
        node.depth = parent.depth + 1;
        parent.children.push(node);
        continue;
      }
    }

    roots.push(node);
  }

  return roots;
}

export function getThreadDescendantCount<T extends { children: Array<T> }>(
  node: T,
): number {
  return node.children.reduce((count, child) => {
    return count + 1 + getThreadDescendantCount(child);
  }, 0);
}

/** Strip all markdown and HTML tags to produce a clean text preview for cards. */
export function stripForumMarkdownForPreview(text: string, maxLength = 160) {
  if (!text) return "";

  // 1. Remove markdown images
  let clean = text.replace(/!\[[^\]]*\]\([^)]*\)/g, " ");

  // 2. Remove markdown links (keep only link text)
  clean = clean.replace(/\[([^\]]+)\]\([^)]*\)/g, "$1");

  // 3. Remove code blocks
  clean = clean.replace(/```[a-z]*\n[\s\S]*?\n```/g, " ");

  // 4. Remove inline code
  clean = clean.replace(/`([^`]+)`/g, "$1");

  // 5. Remove HTML tags
  clean = clean.replace(/<[^>]*>/g, " ");

  // 6. Remove bold/italic markers
  clean = clean.replace(/(\*\*|__)(.*?)\1/g, "$2");
  clean = clean.replace(/(\*|_)(.*?)\1/g, "$2");
  clean = clean.replace(/~~(.*?)~~/g, "$1");

  // 7. Remove headers
  clean = clean.replace(/^#+\s+/gm, "");
  clean = clean.replace(/#+\s+/g, "");

  // 8. Remove list bullets/numbers at the start of lines
  clean = clean.replace(/^[-*+]\s+/gm, "");
  clean = clean.replace(/^\d+\.\s+/gm, "");

  // 9. Remove blockquotes
  clean = clean.replace(/^\s*>\s+/gm, "");

  // 10. Collapse multiple spaces and newlines to a single space
  const singleLine = clean.replace(/\s+/g, " ").trim();

  return singleLine.length > maxLength
    ? `${singleLine.slice(0, maxLength)}…`
    : singleLine;
}
